import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, CheckCircle, AlertTriangle, Lightbulb, Apple } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MindfulShoppingGuide() {
  const navigate = useNavigate();

  const perimeterSections = [
    { name: "Produce", items: ["Leafy greens", "Colorful vegetables", "Fresh fruits", "Fresh herbs"], tip: "Fill half your cart here first" },
    { name: "Proteins", items: ["Wild-caught fish", "Organic chicken", "Grass-fed beef", "Eggs"], tip: "Look for quality over quantity" },
    { name: "Dairy/Alternatives", items: ["Greek yogurt", "Cottage cheese", "Almond milk", "Cheese (in moderation)"], tip: "Check sugar content on flavored options" }
  ];

  const labelRedFlags = [
    { flag: "Sugar in first 3 ingredients", why: "Indicates sugar is a primary ingredient" },
    { flag: "More than 5 ingredients", why: "Usually means it's highly processed" },
    { flag: "Ingredients you can't pronounce", why: "Likely artificial additives" },
    { flag: "Sodium over 600mg per serving", why: "Can contribute to bloating and high blood pressure" },
    { flag: "'Natural flavors'", why: "Vague term that can hide many additives" },
    { flag: "Trans fats or partially hydrogenated oils", why: "Linked to heart disease" }
  ];

  const smartSwaps = [
    { from: "White bread", to: "Whole grain or sprouted bread" },
    { from: "Sugary cereal", to: "Oatmeal or low-sugar granola" },
    { from: "Fruit juice", to: "Whole fruit or infused water" },
    { from: "Chips", to: "Nuts or veggie sticks" },
    { from: "Ice cream", to: "Frozen fruit or Greek yogurt" },
    { from: "Soda", to: "Sparkling water with lemon" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Mindful Grocery Shopping
          </h1>
          <p className="text-xl text-secondary-600">Shop Smarter for a Healthier You</p>
        </div>

        {/* Main Rule */}
        <section className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <ShoppingCart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">The 80/20 Rule</h3>
              <p className="text-green-100 text-lg">
                Spend 80% of your time in the store's perimeter (produce, proteins, dairy) and only 20% in the center aisles. 
                The freshest, most nutritious foods live on the edges.
              </p>
            </div>
          </div>
        </section>

        {/* Perimeter Shopping */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Shop the Perimeter First</h2>
          <div className="space-y-6">
            {perimeterSections.map((section, index) => (
              <div key={index} className="bg-green-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-green-700 mb-3 flex items-center gap-2">
                  <Apple className="w-5 h-5" />
                  {section.name}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-secondary-500 mb-2">Stock Up On:</p>
                    <ul className="space-y-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm text-secondary-700">
                      <Lightbulb className="w-4 h-4 text-amber-500 inline mr-1" />
                      <strong>Tip:</strong> {section.tip}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Label Reading */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Label Red Flags
          </h2>
          <p className="text-secondary-600 mb-4">When checking packaged items, watch out for:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {labelRedFlags.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-4">
                <p className="font-bold text-amber-700 mb-1">{item.flag}</p>
                <p className="text-sm text-secondary-600">{item.why}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Smart Swaps */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Smart Swaps</h2>
          <div className="space-y-3">
            {smartSwaps.map((swap, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-green-50 rounded-xl">
                <span className="text-secondary-500 line-through flex-1">{swap.from}</span>
                <span className="text-2xl">→</span>
                <span className="text-green-700 font-medium flex-1">{swap.to}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MindfulShoppingGuide;
